import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { Heart, MessageCircle, Calendar, MoreHorizontal, Trash2, Edit3, X, Send, Repeat2 } from 'lucide-react';
import { toast } from 'sonner';
import TextOnlyPostArt from './TextOnlyPostArt';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const fmtAgo = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  const s = Math.max(1, Math.floor((Date.now() - d.getTime()) / 1000));
  if (s < 60) return `${s}s`;
  if (s < 3600) return `${Math.floor(s / 60)}m`;
  if (s < 86400) return `${Math.floor(s / 3600)}h`;
  if (s < 604800) return `${Math.floor(s / 86400)}d`;
  return d.toLocaleDateString();
};

/** Facebook-style media grid renderer.
 *  1 item → full bleed (4:5 portrait or square)
 *  2 → side-by-side
 *  3 → 1 large + 2 stacked
 *  4 → 2x2
 *  5+ → 2x2 with "+N" overlay on the 4th tile
 */
function PostMediaGrid({ items, videoRef, postId, caption }) {
  if (!items || items.length === 0) return null;

  const Tile = ({ item, idx, className = '', controls = false, attachVideoRef = false }) => (
    <div className={`relative bg-black overflow-hidden ${className}`} data-testid={`post-tile-${postId}-${idx}`}>
      {item.type === 'video' ? (
        <video
          ref={(el) => {
            if (el) {
              // iOS Safari: setting `muted` as a property is unreliable on initial render.
              // Setting it via the DOM ref + the `defaultMuted` attribute below ensures
              // autoplay actually works.
              el.muted = true;
              el.setAttribute('muted', '');
              el.setAttribute('playsinline', '');
            }
            if (attachVideoRef && videoRef) videoRef.current = el;
          }}
          src={item.url}
          className="w-full h-full object-contain bg-black"
          autoPlay={controls}
          defaultMuted
          muted
          loop
          playsInline
          controls
          controlsList="nodownload"
          preload="metadata"
        />
      ) : (
        <img
          src={item.url}
          alt={caption || 'Post'}
          loading="lazy"
          className="w-full h-full object-cover"
        />
      )}
    </div>
  );

  if (items.length === 1) {
    return (
      <div className="bg-black aspect-[4/5] sm:aspect-square w-full overflow-hidden">
        <Tile item={items[0]} idx={0} className="w-full h-full" controls={items[0].type === 'video'} attachVideoRef />
      </div>
    );
  }
  if (items.length === 2) {
    return (
      <div className="grid grid-cols-2 gap-0.5 bg-black w-full aspect-square">
        {items.map((m, i) => <Tile key={i} item={m} idx={i} className="w-full h-full" />)}
      </div>
    );
  }
  if (items.length === 3) {
    return (
      <div className="grid grid-cols-2 grid-rows-2 gap-0.5 bg-black w-full aspect-square">
        <Tile item={items[0]} idx={0} className="w-full h-full row-span-2" />
        <Tile item={items[1]} idx={1} className="w-full h-full" />
        <Tile item={items[2]} idx={2} className="w-full h-full" />
      </div>
    );
  }
  // 4+
  const visible = items.slice(0, 4);
  const extra = items.length - 4;
  return (
    <div className="grid grid-cols-2 grid-rows-2 gap-0.5 bg-black w-full aspect-square">
      {visible.map((m, i) => (
        <div key={i} className="relative w-full h-full">
          <Tile item={m} idx={i} className="w-full h-full" />
          {i === 3 && extra > 0 && (
            <div className="absolute inset-0 bg-black/60 flex items-center justify-center text-white text-2xl font-bold">
              +{extra}
            </div>
          )}
        </div>
      ))}
    </div>
  );
}


/**
 * Instagram-style post card.
 * - Image OR video media (autoplay-muted, plays only when in view).
 * - Like / Comment toggles.
 * - Book Now button — only when poster is a vendor (`post.can_book`).
 * - Owner can edit caption + delete via … menu.
 */
export default function PostCard({ post, viewer, onChange, onDelete }) {
  const author = post.author || {};
  const isOwner = viewer && viewer.user_id === author.user_id;
  const [liked, setLiked] = useState(post.viewer_liked);
  const [likes, setLikes] = useState(post.likes_count || 0);
  const [showComments, setShowComments] = useState(false);
  const [comments, setComments] = useState([]);
  const [commentText, setCommentText] = useState('');
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(post.caption || '');
  const [menuOpen, setMenuOpen] = useState(false);
  const videoRef = useRef(null);

  // Normalize media — server may return new `media: [...]` or legacy single fields
  const mediaItems = Array.isArray(post.media) && post.media.length
    ? post.media
    : (post.media_url && post.media_type
        ? [{ url: post.media_url, type: post.media_type }]
        : []);

  // Autoplay-muted in-view (TikTok pattern) — only when single-video post
  useEffect(() => {
    if (mediaItems.length !== 1 || mediaItems[0].type !== 'video' || !videoRef.current) return undefined;
    const v = videoRef.current;
    const obs = new IntersectionObserver(
      (entries) => entries.forEach((e) => {
        if (e.isIntersecting && e.intersectionRatio > 0.5) v.play().catch(() => {});
        else v.pause();
      }),
      { threshold: [0, 0.5, 1] },
    );
    obs.observe(v);
    return () => obs.disconnect();
  }, [mediaItems.length, mediaItems[0]?.type]);

  const headers = () => ({
    Authorization: `Bearer ${localStorage.getItem('token')}`,
  });

  const toggleLike = async () => {
    if (!viewer) return toast.error('Sign in to like posts');
    // Optimistic
    setLiked((v) => !v);
    setLikes((c) => (liked ? c - 1 : c + 1));
    try {
      const res = await fetch(`${API}/social/posts/${post.post_id}/like`, { method: 'POST', headers: headers() });
      if (res.ok) {
        const d = await res.json();
        setLiked(d.liked);
        setLikes(d.likes_count);
      } else {
        // rollback
        setLiked((v) => !v);
        setLikes((c) => (liked ? c + 1 : c - 1));
      }
    } catch {
      setLiked((v) => !v);
      setLikes((c) => (liked ? c + 1 : c - 1));
    }
  };

  const loadComments = async () => {
    setShowComments(true);
    try {
      const res = await fetch(`${API}/social/posts/${post.post_id}/comments`);
      if (res.ok) setComments((await res.json()).comments || []);
    } catch {}
  };

  const submitComment = async (e) => {
    e?.preventDefault();
    const txt = commentText.trim();
    if (!txt) return;
    if (!viewer) return toast.error('Sign in to comment');
    try {
      const res = await fetch(`${API}/social/posts/${post.post_id}/comments`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...headers() },
        body: JSON.stringify({ content: txt }),
      });
      const d = await res.json();
      if (res.ok) {
        setComments((cs) => [...cs, d.comment]);
        setCommentText('');
        onChange?.();
      } else toast.error(d.detail || 'Could not post comment');
    } catch { toast.error('Network error'); }
  };

  const removeComment = async (cid) => {
    try {
      const res = await fetch(`${API}/social/comments/${cid}`, { method: 'DELETE', headers: headers() });
      if (res.ok) setComments((cs) => cs.filter((c) => c.comment_id !== cid));
    } catch {}
  };

  const saveCaption = async () => {
    try {
      const res = await fetch(`${API}/social/posts/${post.post_id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', ...headers() },
        body: JSON.stringify({ caption: draft }),
      });
      if (res.ok) { toast.success('Updated'); setEditing(false); onChange?.(); }
      else toast.error('Could not update');
    } catch { toast.error('Network error'); }
  };

  const removePost = async () => {
    if (!confirm('Delete this post?')) return;
    try {
      const res = await fetch(`${API}/social/posts/${post.post_id}`, { method: 'DELETE', headers: headers() });
      if (res.ok) { toast.success('Post deleted'); onDelete?.(post.post_id); }
      else toast.error('Could not delete');
    } catch { toast.error('Network error'); }
  };

  const repost = async () => {
    if (!viewer) return toast.error('Sign in to repost');
    try {
      const res = await fetch(`${API}/social/posts/${post.post_id}/repost`, { method: 'POST', headers: headers() });
      const data = await res.json().catch(() => ({}));
      if (res.ok) { toast.success('Reposted to your profile!'); onChange?.(); }
      else toast.error(data.detail || 'Could not repost');
    } catch { toast.error('Network error'); }
  };

  const profileLink = author.vendor_id ? `/vendor/${author.vendor_id}` : `/u/${author.user_id}`;
  const bookLink = author.vendor_id ? `/vendor/${author.vendor_id}#services` : null;

  return (
    <article
      className="rounded-2xl bg-[#13153A]/80 border border-white/5 backdrop-blur-md overflow-hidden shadow-[0_8px_32px_-12px_rgba(168,85,247,0.4)]"
      data-testid={`post-${post.post_id}`}
    >
      {/* Reposted-from banner */}
      {post.reposted_from && (
        <div className="px-4 pt-3 -mb-1 flex items-center gap-1.5 text-zinc-400 text-[11px]">
          <Repeat2 size={12} className="text-cyan-300" />
          <span>Reposted from <span className="font-semibold text-zinc-200">{post.reposted_from.name}</span></span>
        </div>
      )}
      {/* Header */}
      <header className="flex items-center justify-between px-4 py-3">
        <Link to={profileLink} className="flex items-center gap-3 group" data-testid={`post-author-${post.post_id}`}>
          {author.profile_image ? (
            <img src={author.profile_image} alt={author.name} className="w-9 h-9 rounded-full object-cover border border-white/10" />
          ) : (
            <div className="w-9 h-9 rounded-full bg-gradient-to-br from-fuchsia-500/40 to-cyan-500/40 flex items-center justify-center text-white font-semibold text-sm">
              {(author.name || '?')[0]}
            </div>
          )}
          <div>
            <p className="text-white text-sm font-semibold group-hover:text-fuchsia-300">{author.name}</p>
            <p className="text-zinc-400 text-[11px]">{fmtAgo(post.created_at)}{author.role === 'vendor' ? ' · Vendor' : ''}</p>
          </div>
        </Link>
        {isOwner && (
          <div className="relative">
            <button onClick={() => setMenuOpen((o) => !o)} className="p-2 rounded-lg hover:bg-white/10 text-zinc-300" data-testid={`post-menu-${post.post_id}`}>
              <MoreHorizontal size={16} />
            </button>
            {menuOpen && (
              <div className="absolute right-0 top-9 bg-[#1a1d4a] border border-white/10 rounded-lg shadow-xl z-10 py-1 min-w-[140px]">
                <button onClick={() => { setEditing(true); setMenuOpen(false); }} className="w-full px-3 py-2 text-xs text-white hover:bg-white/5 text-left flex items-center gap-2">
                  <Edit3 size={12} /> Edit caption
                </button>
                <button onClick={() => { removePost(); setMenuOpen(false); }} className="w-full px-3 py-2 text-xs text-red-300 hover:bg-red-500/10 text-left flex items-center gap-2">
                  <Trash2 size={12} /> Delete post
                </button>
              </div>
            )}
          </div>
        )}
      </header>

      {/* Media — Facebook-style multi-grid (1/2/3/4+) OR text-only gradient art */}
      {mediaItems.length > 0 ? (
        <PostMediaGrid items={mediaItems} videoRef={videoRef} postId={post.post_id} caption={post.caption} />
      ) : (
        post.caption && <TextOnlyPostArt postId={post.post_id} caption={post.caption} />
      )}

      {/* Action bar */}
      <div className="flex items-center justify-between px-4 py-3">
        <div className="flex items-center gap-3">
          <button onClick={toggleLike} className="flex items-center gap-1.5 text-zinc-200 hover:text-fuchsia-300" data-testid={`post-like-${post.post_id}`}>
            <Heart size={20} className={liked ? 'fill-fuchsia-400 text-fuchsia-400' : ''} />
            <span className="text-sm font-semibold">{likes}</span>
          </button>
          <button onClick={() => (showComments ? setShowComments(false) : loadComments())} className="flex items-center gap-1.5 text-zinc-200 hover:text-cyan-300" data-testid={`post-comment-toggle-${post.post_id}`}>
            <MessageCircle size={20} />
            <span className="text-sm font-semibold">{post.comments_count || comments.length}</span>
          </button>
          {viewer && !isOwner && (
            <button onClick={repost} className="flex items-center gap-1.5 text-zinc-200 hover:text-emerald-300" data-testid={`post-repost-${post.post_id}`} title="Repost">
              <Repeat2 size={20} />
            </button>
          )}
        </div>
        {post.can_book && bookLink && (
          <Link
            to={bookLink}
            onClick={() => { try { window.metaTrack?.('AddToCart', { content_ids: [author.vendor_id], content_type: 'product', source: 'feed_post' }); } catch {} }}
            className="px-3 py-1.5 rounded-full bg-yellow-400 hover:bg-yellow-300 text-black text-xs font-bold flex items-center gap-1"
            data-testid={`post-book-${post.post_id}`}
          >
            <Calendar size={13} /> Book Now
          </Link>
        )}
      </div>

      {/* Caption (hidden when text-only art already shows it) */}
      <div className="px-4 pb-3">
        {editing ? (
          <div className="space-y-2">
            <textarea value={draft} onChange={(e) => setDraft(e.target.value)} rows={3} maxLength={2200} className="w-full px-3 py-2 rounded-lg bg-white/5 border border-white/10 text-white text-sm" />
            <div className="flex items-center gap-2">
              <button onClick={saveCaption} className="px-3 py-1.5 rounded-lg bg-yellow-400 text-black text-xs font-bold">Save</button>
              <button onClick={() => { setEditing(false); setDraft(post.caption); }} className="px-3 py-1.5 rounded-lg bg-white/5 border border-white/10 text-zinc-300 text-xs">Cancel</button>
            </div>
          </div>
        ) : (
          mediaItems.length > 0 && post.caption && (
            <p className="text-sm text-zinc-200 leading-relaxed whitespace-pre-wrap">
              <span className="font-semibold text-white mr-2">{author.name}</span>{post.caption}
            </p>
          )
        )}
      </div>

      {/* Comments */}
      {showComments && (
        <div className="border-t border-white/5 bg-[#0e1138]/80">
          <div className="max-h-72 overflow-y-auto px-4 py-3 space-y-3">
            {comments.length === 0 && <p className="text-zinc-500 text-xs">Be the first to comment.</p>}
            {comments.map((c) => (
              <div key={c.comment_id} className="flex items-start gap-2.5 group" data-testid={`comment-${c.comment_id}`}>
                {c.author?.profile_image ? (
                  <img src={c.author.profile_image} alt="" className="w-7 h-7 rounded-full object-cover" />
                ) : (
                  <div className="w-7 h-7 rounded-full bg-fuchsia-500/30 flex items-center justify-center text-[11px] text-white font-semibold">{(c.author?.name || '?')[0]}</div>
                )}
                <div className="flex-1 min-w-0">
                  <p className="text-xs text-zinc-200">
                    <span className="font-semibold text-white mr-1.5">{c.author?.name}</span>
                    {c.content}
                  </p>
                  <p className="text-[10px] text-zinc-500 mt-0.5">{fmtAgo(c.created_at)}</p>
                </div>
                {viewer && viewer.user_id === c.user_id && (
                  <button onClick={() => removeComment(c.comment_id)} className="text-zinc-500 hover:text-red-400 opacity-0 group-hover:opacity-100" data-testid={`comment-delete-${c.comment_id}`}>
                    <X size={12} />
                  </button>
                )}
              </div>
            ))}
          </div>
          {viewer && (
            <form onSubmit={submitComment} className="flex items-center gap-2 px-4 py-3 border-t border-white/5">
              <input
                value={commentText}
                onChange={(e) => setCommentText(e.target.value)}
                placeholder="Add a comment…"
                maxLength={600}
                className="flex-1 px-3 py-2 rounded-full bg-white/5 border border-white/10 text-white text-sm focus:outline-none focus:border-fuchsia-500/50"
                data-testid={`comment-input-${post.post_id}`}
              />
              <button type="submit" disabled={!commentText.trim()} className="p-2 rounded-full bg-yellow-400 hover:bg-yellow-300 text-black disabled:opacity-40" data-testid={`comment-submit-${post.post_id}`}>
                <Send size={14} />
              </button>
            </form>
          )}
        </div>
      )}
    </article>
  );
}
